/* eslint-disable */
class PressLogosMarquee extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-marquee-viewport]');
    this.track = this.querySelector('[data-marquee-track]');
    this.group = this.querySelector('[data-marquee-group]');

    if (!this.viewport || !this.track || !this.group) return;

    this.pauseOnHover = this.getAttribute('data-pause-hover') === 'true';
    this.pauseOnTouch = this.getAttribute('data-pause-touch') === 'true';

    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.originalGroupHTML = this.group.innerHTML;

    this.offsetPx = 0;
    this.lastFrameTs = null;
    this.rafId = null;
    this.cycleWidthPx = 0;
    this.gapPx = 0;
    this.viewportWidthPx = 0;

    this.buildBound = this.build.bind(this);
    this.onHoverStartBound = this.onHoverStart.bind(this);
    this.onHoverEndBound = this.onHoverEnd.bind(this);
    this.onPointerDownBound = this.onPointerDown.bind(this);
    this.onPointerUpBound = this.onPointerUp.bind(this);
    this.onReduceMotionChangeBound = this.onReduceMotionChange.bind(this);

    this.setupTouchPause();
    this.setupHoverPause();
    this.setupReducedMotionListener();

    // Rebuild when size changes (responsive).
    this.resizeObserver = new ResizeObserver(() => this.build());
    this.resizeObserver.observe(this.viewport);

    this.build();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.stop();

    if (this.pauseOnHover) {
      this.removeEventListener('mouseenter', this.onHoverStartBound);
      this.removeEventListener('mouseleave', this.onHoverEndBound);
      this.removeEventListener('focusin', this.onHoverStartBound);
      this.removeEventListener('focusout', this.onHoverEndBound);
    }

    if (this.reduceMotionQuery) {
      if (this.reduceMotionQuery.removeEventListener) {
        this.reduceMotionQuery.removeEventListener('change', this.onReduceMotionChangeBound);
      } else if (this.reduceMotionQuery.removeListener) {
        this.reduceMotionQuery.removeListener(this.onReduceMotionChangeBound);
      }
    }

    if (this.pauseOnTouch) {
      this.removeEventListener('pointerdown', this.onPointerDownBound);
      window.removeEventListener('pointerup', this.onPointerUpBound);
      window.removeEventListener('pointercancel', this.onPointerUpBound);
      window.removeEventListener('blur', this.onPointerUpBound);
    }
  }

  setupHoverPause() {
    if (!this.pauseOnHover) return;

    this.addEventListener('mouseenter', this.onHoverStartBound, { passive: true });
    this.addEventListener('mouseleave', this.onHoverEndBound, { passive: true });
    this.addEventListener('focusin', this.onHoverStartBound);
    this.addEventListener('focusout', this.onHoverEndBound);
  }

  setupReducedMotionListener() {
    if (!this.reduceMotionQuery) return;

    if (this.reduceMotionQuery.addEventListener) {
      this.reduceMotionQuery.addEventListener('change', this.onReduceMotionChangeBound);
    } else if (this.reduceMotionQuery.addListener) {
      this.reduceMotionQuery.addListener(this.onReduceMotionChangeBound);
    }
  }

  onReduceMotionChange() {
    this.build();
  }

  setupTouchPause() {
    if (!this.pauseOnTouch) return;

    this.addEventListener('pointerdown', this.onPointerDownBound, { passive: true });
    window.addEventListener('pointerup', this.onPointerUpBound, { passive: true });
    window.addEventListener('pointercancel', this.onPointerUpBound, { passive: true });
    window.addEventListener('blur', this.onPointerUpBound, { passive: true });
  }

  onPointerDown(event) {
    // Only pause on touch/pen interactions (desktop hover handles mouse).
    if (event.pointerType === 'mouse') return;
    this.pause();
  }

  onPointerUp() {
    this.resume();
  }

  onHoverStart() {
    this.pause();
  }

  onHoverEnd() {
    this.resume();
  }

  pause() {
    this.classList.add('is-paused');
    this.stop();
  }

  resume() {
    this.classList.remove('is-paused');
    this.start();
  }

  stop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.lastFrameTs = null;
  }

  start() {
    if (this.rafId) return;
    if (!this.cycleWidthPx) return;
    if (this.classList.contains('is-paused')) return;
    this.rafId = requestAnimationFrame((ts) => this.onFrame(ts));
  }

  onFrame(timestamp) {
    if (!this.cycleWidthPx) {
      this.stop();
      return;
    }

    if (this.lastFrameTs == null) this.lastFrameTs = timestamp;
    const deltaMs = timestamp - this.lastFrameTs;
    this.lastFrameTs = timestamp;

    const durationSec = this.getDurationSeconds();
    const travelPx = this.cycleWidthPx + (this.viewportWidthPx || 0);
    const speedPxPerSec = durationSec > 0 ? travelPx / durationSec : 0;
    const deltaPx = (speedPxPerSec * deltaMs) / 1000;

    this.offsetPx += deltaPx;
    this.recycleItemsIfNeeded();

    this.track.style.transform = `translate3d(${-this.offsetPx}px, 0, 0)`;

    this.rafId = requestAnimationFrame((ts) => this.onFrame(ts));
  }

  getDurationSeconds() {
    const styles = window.getComputedStyle(this);

    const reduced = this.reduceMotionQuery && this.reduceMotionQuery.matches;
    const raw = reduced
      ? styles.getPropertyValue('--press-logos-duration-reduced')
      : styles.getPropertyValue('--press-logos-duration');

    const duration = parseFloat(String(raw).trim());
    return Number.isFinite(duration) && duration > 0 ? duration : 45;
  }

  getGapPx() {
    if (!this.group) return 0;

    const styles = window.getComputedStyle(this.group);
    const raw = styles.getPropertyValue('column-gap') || styles.getPropertyValue('gap') || '0px';
    const gap = parseFloat(String(raw).trim());
    return Number.isFinite(gap) ? gap : 0;
  }

  recycleItemsIfNeeded() {
    if (!this.group || this.offsetPx < 0) return;

    // Move items from the front to the end once they've fully exited left.
    // This makes the marquee repeat immediately without a hard reset.
    let safety = 0;
    while (safety < 50) {
      const first = this.group.firstElementChild;
      if (!first) return;

      const firstWidth = first.getBoundingClientRect().width;
      const moveBy = firstWidth + this.gapPx;
      if (this.offsetPx < moveBy) return;

      this.group.appendChild(first);
      this.offsetPx -= moveBy;
      safety += 1;
    }
  }

  async build() {
    // Reset to original content before rebuilding.
    this.group.innerHTML = this.originalGroupHTML;

    this.stop();
    this.track.style.transform = '';
    this.offsetPx = 0;

    const baseItems = Array.from(this.group.children);
    if (!baseItems.length) return;

    // Wait for images to load so measurements are accurate.
    const imgs = Array.from(this.group.querySelectorAll('img'));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
      )
    );

    const viewportWidth = this.viewport.clientWidth;
    if (!viewportWidth) return;
    this.viewportWidthPx = viewportWidth;

    // If there are only a few logos, duplicate within the same row
    // so there's always another logo ready to enter from the right.
    this.gapPx = this.getGapPx();
    let safety = 0;
    while (this.group.scrollWidth < viewportWidth + 40 && safety < 20) {
      baseItems.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('data-marquee-duplicated-item', 'true');
        clone.removeAttribute('id');
        this.group.appendChild(clone);
      });
      safety += 1;
    }

    this.cycleWidthPx = this.group.scrollWidth;
    if (!this.cycleWidthPx) return;

    // Show logos immediately; as items recycle, they continuously enter from the right edge.
    this.offsetPx = 0;
    this.track.style.transform = 'translate3d(0, 0, 0)';

    this.start();
  }
}

if (!window.customElements.get('press-logos-marquee')) {
  window.customElements.define('press-logos-marquee', PressLogosMarquee);
}
